import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, resolveComponent as _resolveComponent, isRef as _isRef, createVNode as _createVNode, withCtx as _withCtx, createElementVNode as _createElementVNode, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, createTextVNode as _createTextVNode, Fragment as _Fragment, createElementBlock as _createElementBlock, withModifiers as _withModifiers } from "vue"

const _hoisted_1 = /*#__PURE__*/_createElementVNode("p", null, "Укажите точный адрес пункта выдачи карт, либо введите ориентиры в комментарий.", -1)

import { Ref, computed, ref, watch } from 'vue'
import {
  VUserSelect,
  VMunicipalEntitiesSelect,
  VPhoneField,
  DeleteConfirmationDialog,
  AddressSuggester,
} from '../../../../components'
import {
  AppAuthService,
  CardIssuePoint,
  CardIssuePointOrderFields,
  CardIssuePointsService,
  Id,
  MunicipalEntitiesService,
  OrderDirection,
  UpdateCardIssuePointOpts,
  UsersService,
  Permissions,
  AddressSuggesterService,
  AddressLevel,
  AddressSuggestion,
} from '@prodcard/core'
import { createCtx, handleError, removeFieldsFromEntity, subjectOfRF, useWatcherSavedRef } from '../../../../helpers'
import { useVuelidate, ErrorObject } from '@vuelidate/core'
import { required, requiredIf, minValue, helpers } from '@vuelidate/validators'
import { useAuthPermissions } from '../../../../mixins'

interface Props {
  authService: AppAuthService
  usersService: UsersService
  municipalEntitiesService: MunicipalEntitiesService
  cardIssuePointsService: CardIssuePointsService
  addressSuggesterService: AddressSuggesterService
  loading?: boolean
  disabled?: boolean
  cardIssuePoint?: CardIssuePoint
}


export default /*#__PURE__*/_defineComponent({
  __name: 'CardIssuePointForm',
  props: {
    authService: {},
    usersService: {},
    municipalEntitiesService: {},
    cardIssuePointsService: {},
    addressSuggesterService: {},
    loading: { type: Boolean, default: false },
    disabled: { type: Boolean, default: false },
    cardIssuePoint: {}
  },
  emits: ['submit', 'delete'],
  setup(__props: any, { emit }) {

const props = __props;




const { currentMunicipalEntityId, hasPermissions } = useAuthPermissions(props.authService)
const hasManage = computed(() => {
  return hasPermissions(Permissions.CARD_ISSUE_POINTS_MANAGE)
})
const hasMeManage = computed(() => {
  return (
    (props.cardIssuePoint && hasPermissions(Permissions.ME_CARD_ISSUE_POINTS_UPDATE)) ||
    (!props.cardIssuePoint && hasPermissions(Permissions.ME_CARD_ISSUE_POINTS_ADD))
  )
})
const hasUsersView = computed(() => hasPermissions(Permissions.USERS_VIEW) || hasPermissions(Permissions.ME_USERS_VIEW))

const hasDeletePermission = computed(() => {
  return (
    hasManage.value ||
    (props.cardIssuePoint?.municipalEntityId === currentMunicipalEntityId.value &&
      hasPermissions(Permissions.ME_CARD_ISSUE_POINTS_DELETE))
  )
})

const loadNumber = ref(false)
const currentIndexMe: Ref<string | undefined> = useWatcherSavedRef('CurrentIndexMe', undefined)

const cardIssuePointOpts: Ref<UpdateCardIssuePointOpts> = useWatcherSavedRef('CardIssuePointOpts', {
  ...removeFieldsFromEntity(props.cardIssuePoint),
  responsiblePersonId:
    props.cardIssuePoint?.responsiblePersonId === null ? '' : props.cardIssuePoint?.responsiblePersonId,
  municipalEntityId:
    hasMeManage.value && !hasManage.value
      ? props.cardIssuePoint?.municipalEntityId ?? currentMunicipalEntityId.value
      : props.cardIssuePoint?.municipalEntityId,
})

const deleteConfirmation = ref(false)

const rules = computed(() => ({
  municipalEntityId: {
    required: helpers.withMessage('Муниципальное образование не может быть пустым', required),
  },
  locality: {
    required: helpers.withMessage('Населенный пункт не может быть пустым', required),
  },
  number: {
    required: helpers.withMessage('Номер не может быть пустым', required),
    minValue: helpers.withMessage('Номер не может быть меньше 0', minValue(0)),
  },
  street: {
    requiredIf: helpers.withMessage(
      'Улица не может быть пустой',
      requiredIf(() => {
        return !cardIssuePointOpts.value.comment
      }),
    ),
  },
  building: {
    requiredIf: helpers.withMessage(
      'Дом не может быть пустым',
      requiredIf(() => {
        return !cardIssuePointOpts.value.comment
      }),
    ),
  },
  comment: {
    requiredIf: helpers.withMessage(
      'Комментарий не может быть пустым',
      requiredIf(() => {
        return !cardIssuePointOpts.value.street || !cardIssuePointOpts.value.building
      }),
    ),
  },
  windowsCount: {
    required: helpers.withMessage('Количество окон не может быть пустым', required),
    minValue: helpers.withMessage('Количество окон быть меньше 0', minValue(0)),
  },
  phone: {
    phone: helpers.withMessage('Контактный телефон некорректен', () => {
      if (!cardIssuePointOpts.value.phone) return true

      return cardIssuePointOpts.value.phone.length === 11
    }),
  },
}))

const validator = useVuelidate<UpdateCardIssuePointOpts>(rules, cardIssuePointOpts)

const submit = async () => {
  const valid = await validator.value.$validate()

  if (valid && !loadNumber.value) emit('submit', cardIssuePointOpts.value)
}

const getErrorMessages = (errors: ErrorObject[]) => {
  return errors.map((e: ErrorObject) => e.$message).join()
}

const updateNumber = (municipalEntityId?: Id) => {
  if (!municipalEntityId) {
    currentIndexMe.value = undefined
    cardIssuePointOpts.value.number = undefined
    return
  }
  const context = createCtx()

  handleError(async () => {
    const municipalEntity = await props.municipalEntitiesService.findById(context, municipalEntityId)

    if (!municipalEntity) return

    currentIndexMe.value = `${municipalEntity?.index}.`

    if (props.cardIssuePoint && cardIssuePointOpts.value.number) return

    const cardIssuePoints = await props.cardIssuePointsService.findAll(context, {
      paging: { from: 0, count: 20 },
      filter: { municipalEntityId },
      order: { field: CardIssuePointOrderFields.NUMBER, direction: OrderDirection.DESC },
    })
    const number = (cardIssuePoints.items[0]?.number || 0) + 1
    if (props.cardIssuePoint && cardIssuePointOpts.value.number === props.cardIssuePoint?.number) return
    if (cardIssuePointOpts.value.number !== number) cardIssuePointOpts.value.number = number
  }, loadNumber)
}

watch(() => cardIssuePointOpts.value.municipalEntityId, updateNumber, { immediate: true })

const updateAddressWithSuggestion = (suggest: AddressSuggestion) => {
  if (suggest.municipalEntityId && suggest.municipalEntityId !== cardIssuePointOpts.value.municipalEntityId) {
    cardIssuePointOpts.value.municipalEntityId = suggest.municipalEntityId
  }
  if (
    suggest.address[AddressLevel.LOCALITY] &&
    suggest.address[AddressLevel.LOCALITY] !== cardIssuePointOpts.value.locality
  ) {
    cardIssuePointOpts.value.locality = suggest.address[AddressLevel.LOCALITY]
  }
  if (
    suggest.address[AddressLevel.STREET] &&
    suggest.address[AddressLevel.STREET] !== cardIssuePointOpts.value.street
  ) {
    cardIssuePointOpts.value.street = suggest.address[AddressLevel.STREET]
  }
  if (
    suggest.address[AddressLevel.HOUSE] &&
    suggest.address[AddressLevel.HOUSE] !== cardIssuePointOpts.value.building
  ) {
    cardIssuePointOpts.value.building = suggest.address[AddressLevel.HOUSE]
  }
}

return (_ctx: any,_cache: any) => {
  const _component_v_text_field = _resolveComponent("v-text-field")!
  const _component_v_col = _resolveComponent("v-col")!
  const _component_v_textarea = _resolveComponent("v-textarea")!
  const _component_v_row = _resolveComponent("v-row")!
  const _component_v_btn = _resolveComponent("v-btn")!
  const _component_v_form = _resolveComponent("v-form")!

  return (_openBlock(), _createBlock(_component_v_form, {
    onSubmit: _withModifiers(submit, ["prevent"]),
    disabled: _ctx.loading || _ctx.disabled
  }, {
    default: _withCtx(() => [
      _createVNode(_component_v_row, null, {
        default: _withCtx(() => [
          _createVNode(_component_v_col, { cols: "12" }, {
            default: _withCtx(() => [
              _createVNode(_component_v_text_field, {
                modelValue: _unref(subjectOfRF),
                "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => (_isRef(subjectOfRF) ? (subjectOfRF).value = $event : null)),
                label: "Субъект РФ*",
                readonly: true
              }, null, 8, ["modelValue"])
            ]),
            _: 1
          }),
          _createVNode(_component_v_col, {
            cols: "12",
            md: "6"
          }, {
            default: _withCtx(() => [
              _createVNode(_unref(VMunicipalEntitiesSelect), {
                modelValue: _unref(cardIssuePointOpts).municipalEntityId,
                "onUpdate:modelValue": [
                  _cache[1] || (_cache[1] = ($event: any) => ((_unref(cardIssuePointOpts).municipalEntityId) = $event)),
                  _cache[2] || (_cache[2] = ($event: any) => (_unref(cardIssuePointOpts).responsiblePersonId = ''))
                ],
                "municipal-entities-service": _ctx.municipalEntitiesService,
                "error-messages": getErrorMessages(_unref(validator).municipalEntityId.$errors),
                disabled: _ctx.disabled || _ctx.loading,
                readonly: !hasManage.value && hasMeManage.value,
                label: "Муниципальное образование*"
              }, null, 8, ["modelValue", "municipal-entities-service", "error-messages", "disabled", "readonly"])
            ]),
            _: 1
          }),
          _createVNode(_component_v_col, {
            cols: "12",
            md: "6"
          }, {
            default: _withCtx(() => [
              _createVNode(_component_v_text_field, {
                modelValue: _unref(cardIssuePointOpts).number,
                "onUpdate:modelValue": _cache[3] || (_cache[3] = ($event: any) => ((_unref(cardIssuePointOpts).number) = $event)),
                modelModifiers: { number: true },
                "error-messages": getErrorMessages(_unref(validator).number.$errors),
                prefix: _unref(currentIndexMe),
                readonly: loadNumber.value,
                loading: loadNumber.value,
                type: "number",
                label: "Номер пункта*"
              }, null, 8, ["modelValue", "error-messages", "prefix", "readonly", "loading"])
            ]),
            _: 1
          }),
          _createVNode(_component_v_col, { cols: "12" }, {
            default: _withCtx(() => [
              _createVNode(_unref(AddressSuggester), {
                modelValue: _unref(cardIssuePointOpts).locality,
                "onUpdate:modelValue": _cache[4] || (_cache[4] = ($event: any) => ((_unref(cardIssuePointOpts).locality) = $event)),
                "address-suggester-service": _ctx.addressSuggesterService,
                "municipal-entity-id": _unref(cardIssuePointOpts).municipalEntityId,
                level: _unref(AddressLevel).LOCALITY,
                "error-messages": getErrorMessages(_unref(validator).locality.$errors),
                label: "Населенный пункт*",
                "onUpdate:selectedValue": updateAddressWithSuggestion
              }, null, 8, ["modelValue", "address-suggester-service", "municipal-entity-id", "level", "error-messages"])
            ]),
            _: 1
          }),
          _createVNode(_component_v_col, { cols: "12" }, {
            default: _withCtx(() => [
              _hoisted_1
            ]),
            _: 1
          }),
          _createVNode(_component_v_col, {
            cols: "12",
            sm: "6",
            md: "8"
          }, {
            default: _withCtx(() => [
              _createVNode(_unref(AddressSuggester), {
                modelValue: _unref(cardIssuePointOpts).street,
                "onUpdate:modelValue": _cache[5] || (_cache[5] = ($event: any) => ((_unref(cardIssuePointOpts).street) = $event)),
                "address-suggester-service": _ctx.addressSuggesterService,
                "municipal-entity-id": _unref(cardIssuePointOpts).municipalEntityId,
                "other-fields": { [_unref(AddressLevel).LOCALITY]: _unref(cardIssuePointOpts).locality },
                level: _unref(AddressLevel).STREET,
                "error-messages": getErrorMessages(_unref(validator).street.$errors),
                label: "Улица*",
                "onUpdate:selectedValue": updateAddressWithSuggestion
              }, null, 8, ["modelValue", "address-suggester-service", "municipal-entity-id", "other-fields", "level", "error-messages"])
            ]),
            _: 1
          }),
          _createVNode(_component_v_col, {
            cols: "12",
            sm: "6",
            md: "4"
          }, {
            default: _withCtx(() => [
              _createVNode(_unref(AddressSuggester), {
                modelValue: _unref(cardIssuePointOpts).building,
                "onUpdate:modelValue": _cache[6] || (_cache[6] = ($event: any) => ((_unref(cardIssuePointOpts).building) = $event)),
                "address-suggester-service": _ctx.addressSuggesterService,
                "municipal-entity-id": _unref(cardIssuePointOpts).municipalEntityId,
                "other-fields": {
            [_unref(AddressLevel).LOCALITY]: _unref(cardIssuePointOpts).locality,
            [_unref(AddressLevel).STREET]: _unref(cardIssuePointOpts).street,
          },
                level: _unref(AddressLevel).HOUSE,
                "error-messages": getErrorMessages(_unref(validator).building.$errors),
                label: "Дом*",
                "onUpdate:selectedValue": updateAddressWithSuggestion
              }, null, 8, ["modelValue", "address-suggester-service", "municipal-entity-id", "other-fields", "level", "error-messages"])
            ]),
            _: 1
          }),
          _createVNode(_component_v_col, { cols: "12" }, {
            default: _withCtx(() => [
              _createVNode(_component_v_textarea, {
                modelValue: _unref(cardIssuePointOpts).comment,
                "onUpdate:modelValue": _cache[7] || (_cache[7] = ($event: any) => ((_unref(cardIssuePointOpts).comment) = $event)),
                "error-messages": getErrorMessages(_unref(validator).comment.$errors),
                label: "Комментарий",
                "auto-grow": ""
              }, null, 8, ["modelValue", "error-messages"])
            ]),
            _: 1
          }),
          _createVNode(_component_v_col, {
            cols: "12",
            sm: "6",
            md: "8"
          }, {
            default: _withCtx(() => [
              _createVNode(_component_v_text_field, {
                modelValue: _unref(cardIssuePointOpts).openingHours,
                "onUpdate:modelValue": _cache[8] || (_cache[8] = ($event: any) => ((_unref(cardIssuePointOpts).openingHours) = $event)),
                label: "Режим работы"
              }, null, 8, ["modelValue"])
            ]),
            _: 1
          }),
          _createVNode(_component_v_col, {
            cols: "12",
            sm: "6",
            md: "4"
          }, {
            default: _withCtx(() => [
              _createVNode(_component_v_text_field, {
                modelValue: _unref(cardIssuePointOpts).windowsCount,
                "onUpdate:modelValue": _cache[9] || (_cache[9] = ($event: any) => ((_unref(cardIssuePointOpts).windowsCount) = $event)),
                modelModifiers: { number: true },
                "error-messages": getErrorMessages(_unref(validator).windowsCount.$errors),
                label: "Количество окон*",
                type: "number"
              }, null, 8, ["modelValue", "error-messages"])
            ]),
            _: 1
          }),
          _createVNode(_component_v_col, {
            cols: "12",
            md: hasUsersView.value ? 6 : 12
          }, {
            default: _withCtx(() => [
              _createVNode(_unref(VPhoneField), {
                phone: _unref(cardIssuePointOpts).phone,
                "onUpdate:phone": _cache[10] || (_cache[10] = ($event: any) => ((_unref(cardIssuePointOpts).phone) = $event)),
                "error-messages": getErrorMessages(_unref(validator).phone.$errors),
                label: "Контактный телефон"
              }, null, 8, ["phone", "error-messages"])
            ]),
            _: 1
          }, 8, ["md"]),
          (hasUsersView.value)
            ? (_openBlock(), _createBlock(_component_v_col, {
                key: 0,
                cols: "12",
                md: "6"
              }, {
                default: _withCtx(() => [
                  _createVNode(_unref(VUserSelect), {
                    "model-value": _unref(cardIssuePointOpts).responsiblePersonId || undefined,
                    "additional-filter-data": { municipalEntityId: _unref(cardIssuePointOpts).municipalEntityId },
                    "users-service": _ctx.usersService,
                    disabled: _ctx.loading || _ctx.disabled || _unref(validator).municipalEntityId.$invalid,
                    label: "Ответственное лицо",
                    "onUpdate:modelValue": _cache[11] || (_cache[11] = 
            val =>
              val ? (_unref(cardIssuePointOpts).responsiblePersonId = val) : (_unref(cardIssuePointOpts).responsiblePersonId = null)
          )
                  }, null, 8, ["model-value", "additional-filter-data", "users-service", "disabled"])
                ]),
                _: 1
              }))
            : _createCommentVNode("", true)
        ]),
        _: 1
      }),
      _createVNode(_component_v_row, { justify: "end" }, {
        default: _withCtx(() => [
          (_ctx.cardIssuePoint && hasDeletePermission.value)
            ? (_openBlock(), _createElementBlock(_Fragment, { key: 0 }, [
                _createVNode(_component_v_col, { cols: "auto" }, {
                  default: _withCtx(() => [
                    _createVNode(_component_v_btn, {
                      color: "red-accent-4",
                      variant: "outlined",
                      loading: _ctx.loading,
                      disabled: _ctx.disabled,
                      onClick: _cache[12] || (_cache[12] = ($event: any) => (deleteConfirmation.value = true))
                    }, {
                      default: _withCtx(() => [
                        _createTextVNode(" Удалить ")
                      ]),
                      _: 1
                    }, 8, ["loading", "disabled"])
                  ]),
                  _: 1
                }),
                _createVNode(_unref(DeleteConfirmationDialog), {
                  modelValue: deleteConfirmation.value,
                  "onUpdate:modelValue": _cache[13] || (_cache[13] = ($event: any) => ((deleteConfirmation).value = $event)),
                  onAgree: _cache[14] || (_cache[14] = 
            () => {
              emit('delete', _ctx.cardIssuePoint?.id)
              deleteConfirmation.value = false
            }
          )
                }, {
                  title: _withCtx(() => [
                    _createTextVNode(" Подтвердите действие ")
                  ]),
                  _: 1
                }, 8, ["modelValue"])
              ], 64))
            : _createCommentVNode("", true),
          _createVNode(_component_v_col, { cols: "auto" }, {
            default: _withCtx(() => [
              _createVNode(_component_v_btn, {
                type: "submit",
                color: "primary",
                loading: _ctx.loading,
                disabled: _ctx.disabled
              }, {
                default: _withCtx(() => [
                  _createTextVNode(" Сохранить ")
                ]),
                _: 1
              }, 8, ["loading", "disabled"])
            ]),
            _: 1
          })
        ]),
        _: 1
      })
    ]),
    _: 1
  }, 8, ["onSubmit", "disabled"]))
}
}

})